import useSWR from "swr";
import { getCatalogOptions, type CatalogOption } from "@/lib/api";

export interface CatalogOptionsState {
  options: CatalogOption[];
  /** Whether the provider served these from cache (informational). */
  cached: boolean;
  isLoading: boolean;
  error: unknown;
}

/**
 * Resolve account-scoped, dynamic options for a `options_source` (e.g.
 * "elevenlabs_voices") via GET /api/catalog/{source}.
 *
 * Pass `undefined` to disable (no `options_source` on the param) — the request
 * is skipped and `options` stays empty. Revalidation is largely off (catalog
 * data is stable per session) and errors are NOT retried, so the caller can
 * fall back to the param's static `options` on `error` or an empty result.
 */
export function useCatalogOptions(source: string | undefined): CatalogOptionsState {
  const { data, error, isLoading } = useSWR(
    source ? ["catalog", source] : null,
    () => getCatalogOptions(source as string),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 5 * 60_000,
    }
  );

  return {
    options: data?.options ?? [],
    cached: data?.cached ?? false,
    isLoading: Boolean(source) && isLoading,
    error,
  };
}
